<template>
  <div class="o-media-rotator">
    <div v-if="mediaItems.length > 0" class="o-media-rotator-media-container">
      <div
        v-for="(item, i) in mediaItems"
        :key="`image_${i}`"
        :class="showItem(i)"
      >
        <!-- Image type -->
        <div
          v-if="item.type !== undefined && item.type == 'image'"
          class="media-overlay lightbox-entry"
          :style="{ backgroundImage: `url(${item.image_url})` }"
          @click="showMediaItem(item.id)"
        />

        <!-- Audio type -->
        <div
          v-if="item.type !== undefined && item.type == 'audio'"
          class="media-overlay"
          :style="{ backgroundImage: `url(${item.image_url})` }"
        >
          <template>
            <div class="playing-overlay" :class="getAppName()" />
            <BaseIcon multiplatform icon="play-audio" size="100px" />
            <span v-if="item.title" class="mt-2">
              <h2>{{ item.title }}</h2>
            </span>
            <div class="audio-controls mt-4">
              <vue-slider
                class="audio-scrubber"
                dot-size="12"
                tooltip="none"
                :max="audioDuration"
                :value="audioCurrentTime"
                @change="setAudioTime"
              />
              <div class="audio-position">
                {{ formatTime(audioCurrentTime) }} /
                {{ formatTime(audioDuration) }}
              </div>
              <div class="audio-buttons mt-4">
                <div @click="handleAudioPrev()">
                  <BaseIcon icon="prev-10s" size="35px" />
                </div>
                <div @click="handleAudioClick()">
                  <BaseIcon
                    multiplatform
                    :icon="playAudio ? 'pause' : 'play'"
                    size="35px"
                  />
                </div>
                <div @click="handleAudioNext()">
                  <BaseIcon icon="next-10s" size="35px" />
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Video type -->
        <div
          v-if="item.type !== undefined && item.type == 'video'"
          :style="{ backgroundImage: `url(${item.image_url})` }"
          class="media-overlay"
          :class="{ videoPlaying: playVideo }"
          @click="handleVideoClick(item)"
        >
          <template v-if="playVideoIndex !== i">
            <div class="playing-overlay" :class="getAppName()" />
            <BaseIcon multiplatform icon="play-video" hover size="100px" />
            <span v-if="item.title" class="mt-2">
              <h2>{{ item.title }}</h2>
            </span>
          </template>
        </div>
      </div>
    </div>

    <div class="o-media-rotator__bullets">
      <div
        v-if="mediaItems.length > 1"
        class="o-media-rotator__bullets-container"
      >
        <svg
          v-for="i in mediaItems.length"
          :key="`bullet_${i}`"
          viewBox="0 0 12 12"
          width="11"
          height="11"
          xmlns="http://www.w3.org/2000/svg"
          @click="selectMedia(i - 1)"
        >
          <circle
            v-if="activeIndex === i - 1"
            cx="6"
            cy="6"
            r="5"
            class="u-bullet_selected"
          />
          <circle
            v-if="activeIndex !== i - 1"
            cx="6"
            cy="6"
            r="5"
            class="u-bullet"
          />
        </svg>
      </div>
      <div class="share-link-container">
        <slot name="share-link-icon" />
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import { appIdentifier } from "@/utils/appIdentifier.js";

export default {
  name: "MediaRotater",
  components: {
    VueSlider
  },
  props: {
    mediaItems: {
      type: Array,
      default: () => []
    },
    controls: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      playVideo: false,
      playVideoIndex: null,
      playAudio: false,
      activeIndex: 0,
      audioPlayer: new Audio(),
      audioDuration: 0,
      audioCurrentTime: 0
    };
  },
  watch: {
    mediaItems: {
      immediate: true,
      handler: function(value) {
        this.selectMedia(0);
        this.$root.$emit("lightboxSetMediaItems", value);
      }
    }
  },
  mounted() {
    this.audioPlayer.addEventListener("timeupdate", () => {
      this.audioDuration = Math.floor(this.audioPlayer.duration);
      this.audioCurrentTime = Math.floor(this.audioPlayer.currentTime);
    });

    this.$root.$on("closeVideoLightbox", () => {
      this.playVideoIndex = null;
    });

    this.$once("hook:beforeDestroy", function() {
      this.$root.$off("closeVideoLightbox");
    });
  },
  methods: {
    formatTime(duration) {
      const minutes = String(Math.floor(duration / 60)).padStart(2, "0");
      const seconds = String(Math.floor(duration % 60)).padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    getAppName() {
      return appIdentifier();
    },
    showItem(i) {
      return {
        "o-media-rotator__item_is-visible": this.activeIndex === i
      };
    },
    selectMedia(index) {
      this.activeIndex = index;
      this.playVideoIndex = null;
      this.playAudio = false;
      this.playVideo = false;
      this.setAudioSource();
    },
    handleVideoClick(video) {
      this.playVideoIndex = this.activeIndex;
      this.$root.$emit("openVideoLightbox", video);
    },
    handleAudioClick() {
      if (!this.playAudio) {
        this.audioPlayer.play();
        this.playAudio = true;
      } else {
        this.playAudio = false;
        this.audioPlayer.pause();
      }
    },
    handleAudioPrev() {
      this.audioPlayer.currentTime -= 10;
    },
    handleAudioNext() {
      this.audioPlayer.currentTime += 10;
    },
    showMediaItem(id) {
      this.$root.$emit("lightboxShowMediaItem", id);
    },
    setAudioSource() {
      this.audioPlayer.pause();
      this.audioDuration = 0;
      this.audioCurrentTime = 0;
      this.audioPlayer.src = this.mediaItems[this.activeIndex].audio_url;
      this.audioPlayer.load();
    },
    setAudioTime(time) {
      this.audioPlayer.currentTime = time;
    }
  }
};
</script>

<style lang="scss" scoped>
.audio-controls {
  width: 200px;

  .audio-position {
    color: white;
    text-align: right;
    font-size: 14px;
    font-family: "AWConquerorCarved-One";
  }

  .audio-scrubber {
    ::v-deep .vue-slider-rail {
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.25);

      .vue-slider-process {
        border-radius: 2px;
        background: rgba(255, 255, 255, 1);
      }

      .vue-slider-dot {
        background: var(--v-secondary-base);
        border-radius: 6px;
      }
    }
  }

  .audio-buttons {
    display: flex;
    justify-content: space-around;

    > div {
      cursor: pointer;
    }
  }
}

.videoPlaying {
  background: black !important;
}

.share-link-container {
  position: absolute;
  right: 5px;
  top: 0;
}

.o-media-rotator {
  position: relative;
  height: 100%;
  width: 100%;

  &-media-container {
    height: 325px;

    @media (max-width: 1367px) {
      height: 275px;
    }
  }

  audio {
    margin: 0 auto;
  }

  .media-overlay {
    position: relative;
    display: none;
    height: 100%;
    width: 100%;
    background-color: var(--v-primary-base);
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    justify-content: center;
    flex-direction: column;
    align-items: center;

    &.lightbox-entry {
      cursor: pointer;
    }

    & > * {
      z-index: 10;
    }
  }

  .playing-overlay {
    border-top-left-radius: 120px;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 5;

    &.huygens {
      opacity: 0.8;
      background: var(--0-f-2836, #0f2836);
    }

    &.canals5D {
      background: rgba(39, 62, 80, 0.8);
    }

    &.dordrecht5D {
      background: rgba(53, 133, 151, 0.8);
    }
  }

  .audio-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__bullets {
    display: flex;
    margin: 5px 0 10px;
    height: 50px;
    width: 100%;
    position: relative;

    &-container {
      display: flex;
      align-items: center;
      width: 50%;
      margin: 10px auto;
      align-items: center;
      justify-content: center;
    }

    .u-bullet {
      color: #ffffff80;
      fill: #ffffff80;
    }

    .u-bullet_selected {
      color: #e3b2b2;
      fill: #e3b2b2;
    }

    svg {
      margin: 0px 3px;

      &:hover {
        cursor: pointer;
      }
    }
  }

  &__item {
    width: 100%;
    z-index: 1;

    &_is-visible {
      height: 100%;
      min-height: inherit;

      img {
        display: block;
      }

      audio {
        display: block;
      }

      .vimeo-player {
        display: block;
      }

      .media-overlay {
        display: flex;
        min-height: inherit;
      }
    }
  }
}
</style>
